import { type ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { RedemptionModalOrderData, TSignupElement } from '@shared/dream-components/src';

import { usePageContext } from '~/context/usePageContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useEmail from '~/hooks/useEmail';
import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import useToast, { type ToastData } from '~/hooks/useToast';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { RootLoaderData } from '~/root';
import DataLayer from '~/services/DataLayer';

import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

import ActionForm from '../ActionForm';
import StatusInputs from '../Forms/StatusInputs';
import ClientSideInputs from './_components/ClientSideInputs';
import ServerSideInputs from './_components/ServerSideInputs';
import useSignupSubmit from './_hooks/useSignupSubmit';

type RenderProps = {
  setEmail: (email: string) => void;
  isLoading: boolean;
};

type SignupActionProps = {
  id: string;
  signupFlowId?: string;
  automationIds?: string; // Ex: `1,2,3` (not an array)
  premiumOfferId?: string;
  redirectUrl?: string;
  customFormType?: TSignupElement['attrs']['customFormType'];
  children: (props: RenderProps) => ReactNode;
  onSuccess?: (data: { order?: RedemptionModalOrderData; toast?: ToastData }) => void;
  shouldSkipRedirectsAfterSuccess?: boolean;
};

const SignupAction = ({
  id,
  signupFlowId,
  automationIds,
  premiumOfferId,
  redirectUrl,
  customFormType,
  children,
  onSuccess,
  shouldSkipRedirectsAfterSuccess = false,
}: SignupActionProps) => {
  const { handleCloseModal, isModalOpen } = useGlobalModals();
  const fallbackId = useId();
  const [, setEmail] = useEmail();
  const fetcher = useNonRevalidatingFetcher<() => Promise<{ toast?: ToastData }>>();
  const { t } = useTranslation();
  const { publication, site } = useRouteLoaderData('root') as RootLoaderData;
  const shouldConsentToTOS = !!site.viewable_site_version.settings.terms_of_service_enabled;
  const { cookies, visitToken, referralId, setCookie, currentPath, setOpenPageModalType, openPageModalType } =
    usePageContext();
  const postPageContext = usePostPageContext();
  const [isLoading, setIsLoading] = useState(false);
  const { startRedirects } = useSignupRedirects();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const offerId = premiumOfferId || searchParams.get('offer_id') || undefined;
  const formId = id || fallbackId;

  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);

  const sendableId = postPageContext?.postId;
  const sendableType = sendableId ? 'post' : undefined;

  const { double_opt_required: isDoubleOptRequired, has_referral_program: isReferralProgram } = publication;

  const shouldDoubleOpt = Boolean(isDoubleOptRequired || (isReferralProgram && referralId));

  const { handleSubmit } = useSignupSubmit({
    formId,
    shouldConsentToTOS,
    onError: () => {
      setIsLoading(false);
    },
    fetcher,
    sendableId,
    sendableType,
  });

  const getToastMessage = () => {
    if (publication.require_subscriber_approval) {
      return t('notifications.subscribe.require_approval');
    }

    if (publication.double_opt_required) {
      return t('notifications.subscribe.confirm');
    }

    return t('notifications.subscribe.subscribed');
  };

  const handleSuccess = (data: any) => {
    window._pxFormId = undefined;

    const isPostPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/p/');
    const isFromPageModal = openPageModalType !== null && ['email_capture', 'content_gate'].includes(openPageModalType);

    setIsLoading(false);
    setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });
    formRef.current?.reset();
    setOpenPageModalType(null);

    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    // On post pages, only trigger signup flow if explicitly configured
    if (isPostPage && (!signupFlowId || signupFlowId === 'none')) {
      if (isModalOpen) {
        handleCloseModal({ preventScrollReset: true });
      }
      onSuccess?.(data);
      return;
    }

    // Close modal when signup came from a page modal with no signup flow configured
    if (isFromPageModal && (!signupFlowId || signupFlowId === 'none') && isModalOpen) {
      handleCloseModal({ preventScrollReset: true });
    }

    /**
      SignupAction is used without a child being SignupElement
      Thus we need to skip the redirects after success
      Examples:
        - Free product checkout modal: When a visitor signs up to a free product, we don't want to redirect to the signup flow
          but show the redemption modal instead
    **/
    if (shouldSkipRedirectsAfterSuccess) {
      onSuccess?.(data);
      return;
    }

    if (data?.signupFlow) {
      startRedirects({ ...data.signupFlow, subscriberId: data.subscriberId, subscriptionUuid: data.subscriptionUuid });
      return;
    }

    if (data?.redirectTo) {
      window.location.href = data.redirectTo;
      return;
    }

    onSuccess?.(data);
  };

  const handleError = (_data: any) => {
    window._pxFormId = undefined;
    setIsLoading(false);
  };

  useToast(fetcher?.data?.toast);

  return (
    <ActionForm
      id={formId}
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/create"
      onSubmit={(e) => {
        setIsLoading(true);
        handleSubmit(e);
      }}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <StatusInputs name="subscribe" successMessage={getToastMessage()} errorMessage={t('errors.default')} />
      <ClientSideInputs
        premiumOfferId={offerId}
        customFormType={customFormType}
        visitToken={visitToken}
        referralId={referralId}
        boostAffiliateRecommendingPublicationId={boostAffiliateRecommendingPublicationId}
        fallbackPath={currentPath}
        redirectUrl={redirectUrl}
      />
      <ServerSideInputs shouldDoubleOpt={shouldDoubleOpt} signupFlowId={signupFlowId} automationIds={automationIds} />
      {children({ isLoading, setEmail })}
    </ActionForm>
  );
};

export default SignupAction;
